import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, KeyRound, ShieldCheck, X, Eye, EyeOff, UserCheck, UserX } from 'lucide-react';
import {
  listUsers,
  createUser,
  updateUser,
  deleteUser,
  changeUserPassword,
  type CreateUserPayload,
  type UpdateUserPayload,
} from '../../services/UserManagementService';
import { getProfiles } from '../../services/ProfileService';
import { Modal, Button } from '../../components/ui';
import { useAuth } from '../../context/AuthContext';
import type { User } from '../../types/@types/user';

// ── Constants ──────────────────────────────────────────────────────────────────

const ROLES = [
  { value: 'admin',   label: 'Administrador', color: 'bg-purple-100 text-purple-800' },
  { value: 'cashier', label: 'Cajero',         color: 'bg-blue-100 text-blue-800' },
  { value: 'kitchen', label: 'Cocina',          color: 'bg-amber-100 text-amber-800' },
];

function getRoleInfo(role?: string) {
  return ROLES.find(r => r.value === role) ?? { value: role ?? '', label: role ?? '—', color: 'bg-muted text-text-muted' };
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function PasswordInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder ?? 'Contraseña'}
        className="w-full rounded-lg border border-border bg-bg px-3 py-2 pr-10 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
      >
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
}

function FormField({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-text">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

// ── User form modal ────────────────────────────────────────────────────────────

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  editUser?: User | null;
}

function UserFormModal({ open, onClose, editUser }: UserFormProps) {
  const qc = useQueryClient();
  const isEdit = !!editUser;

  const [name, setName] = useState(editUser?.name ?? '');
  const [email, setEmail] = useState(editUser?.email ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<string>(editUser?.role ?? 'cashier');
  const [phone, setPhone] = useState(editUser?.phone ?? '');
  const [status, setStatus] = useState<string>(editUser?.status ?? 'active');
  const [profileId, setProfileId] = useState<string>(editUser?.profileId ? String(editUser.profileId) : '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: profiles = [] } = useQuery({ queryKey: ['profiles'], queryFn: getProfiles });

  React.useEffect(() => {
    if (open) {
      setName(editUser?.name ?? '');
      setEmail(editUser?.email ?? '');
      setPassword('');
      setRole(editUser?.role ?? 'cashier');
      setPhone(editUser?.phone ?? '');
      setStatus(editUser?.status ?? 'active');
      setProfileId(editUser?.profileId ? String(editUser.profileId) : '');
      setErrors({});
    }
  }, [open, editUser]);

  const createMutation = useMutation({
    mutationFn: createUser,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); onClose(); },
    onError: (e: any) => setErrors(e.response?.data?.errors ?? { general: 'Error al crear usuario.' }),
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateUserPayload }) => updateUser(id, payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); onClose(); },
    onError: (e: any) => setErrors(e.response?.data?.errors ?? { general: 'Error al actualizar.' }),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  function validate() {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'El nombre es requerido.';
    if (!email.trim()) e.email = 'El email es requerido.';
    if (!isEdit && !password) e.password = 'La contraseña es requerida.';
    if (password && password.length < 6) e.password = 'Mínimo 6 caracteres.';
    return e;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const pId = profileId ? Number(profileId) : undefined;
    if (isEdit) {
      const payload: UpdateUserPayload = { name, email, role: role as any, phone, status: status as any, profile_id: pId };
      updateMutation.mutate({ id: editUser!.id, payload });
    } else {
      const payload: CreateUserPayload = { name, email, password, role: role as any, phone, status: status as any, profile_id: pId };
      createMutation.mutate(payload);
    }
  }

  return (
    <Modal open={open} onClose={onClose} titleId="user-form-modal" className="max-w-lg">
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <h2 className="text-lg font-semibold text-text">
          {isEdit ? 'Editar usuario' : 'Nuevo usuario'}
        </h2>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-9 w-9 rounded-full">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4 px-6 py-5">
          {errors.general && (
            <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{errors.general}</p>
          )}
          <FormField label="Nombre completo" error={errors.name}>
            <input
              type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="Juan García"
              className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </FormField>
          <FormField label="Email" error={errors.email}>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="juan@correo.com"
              className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </FormField>
          {!isEdit && (
            <FormField label="Contraseña" error={errors.password}>
              <PasswordInput value={password} onChange={setPassword} placeholder="Mínimo 6 caracteres" />
            </FormField>
          )}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Rol">
              <select
                value={role} onChange={e => setRole(e.target.value)}
                className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </FormField>
            <FormField label="Estado">
              <select
                value={status} onChange={e => setStatus(e.target.value)}
                className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
            </FormField>
          </div>
          <FormField label="Perfil de permisos">
            <select
              value={profileId} onChange={e => setProfileId(e.target.value)}
              className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">— Sin perfil asignado —</option>
              {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </FormField>
          <FormField label="Teléfono (opcional)">
            <input
              type="text" value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="+57 300 000 0000"
              className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </FormField>
        </div>
        <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" isLoading={isPending} disabled={isPending}>
            {isEdit ? 'Guardar cambios' : 'Crear usuario'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Change password modal ──────────────────────────────────────────────────────

function ChangePasswordModal({ user, onClose }: { user: User | null; onClose: () => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: () => changeUserPassword(user!.id, password),
    onSuccess: () => setSuccess(true),
    onError: () => setError('Error al cambiar la contraseña.'),
  });

  React.useEffect(() => {
    if (!user) { setPassword(''); setError(''); setSuccess(false); }
  }, [user]);

  return (
    <Modal open={!!user} onClose={onClose} titleId="change-password-modal" className="max-w-sm">
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <h2 className="text-base font-semibold text-text">Cambiar contraseña</h2>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-9 w-9 rounded-full">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="px-6 py-5 space-y-4">
        <p className="text-sm text-text-muted">Usuario: <span className="font-medium text-text">{user?.name}</span></p>
        {success ? (
          <p className="rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700">✓ Contraseña actualizada correctamente.</p>
        ) : (
          <>
            <PasswordInput value={password} onChange={v => { setPassword(v); setError(''); }} placeholder="Nueva contraseña (mín. 6 caracteres)" />
            {error && <p className="text-xs text-red-500">{error}</p>}
          </>
        )}
      </div>
      <div className="flex justify-end gap-3 border-t border-border px-6 py-4">
        <Button variant="outline" onClick={onClose}>Cerrar</Button>
        {!success && (
          <Button
            onClick={() => { if (password.length < 6) { setError('Mínimo 6 caracteres.'); return; } mutation.mutate(); }}
            isLoading={mutation.isPending} disabled={mutation.isPending}
          >
            Cambiar
          </Button>
        )}
      </div>
    </Modal>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function UsersPage() {
  const { user: me } = useAuth();
  const qc = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [pwUser, setPwUser] = useState<User | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<User | null>(null);

  const { data: users = [], isLoading, isError } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: listUsers,
    staleTime: 30_000,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); setConfirmDelete(null); },
  });

  const toggleStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'inactive' }) =>
      updateUser(id, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  return (
    <div className="min-h-screen bg-bg p-4 md:p-8">
      <div className="mx-auto max-w-5xl space-y-6">

        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text">Gestión de Usuarios</h1>
            <p className="text-sm text-text-muted">{users.length} usuario{users.length !== 1 ? 's' : ''} registrado{users.length !== 1 ? 's' : ''}</p>
          </div>
          <Button onClick={() => { setEditUser(null); setShowForm(true); }} className="inline-flex items-center gap-2">
            <Plus size={16} />
            Nuevo usuario
          </Button>
        </div>

        {/* Role legend */}
        <div className="flex flex-wrap gap-2">
          {ROLES.map(r => (
            <span key={r.value} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${r.color}`}>
              <ShieldCheck size={12} />
              {r.label}
            </span>
          ))}
        </div>

        {/* List */}
        <div className="rounded-2xl border border-border bg-surface shadow-card overflow-hidden">
          {isLoading ? (
            <div className="space-y-0">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 border-b border-border px-6 py-5">
                  <div className="h-10 w-10 animate-pulse rounded-full bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-40 animate-pulse rounded bg-muted" />
                    <div className="h-3 w-56 animate-pulse rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-sm text-amber-700">
              <p className="font-semibold">Error al cargar usuarios</p>
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16">
              <p className="text-sm text-text-muted">Sin usuarios registrados</p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {users.map(u => {
                const roleInfo = getRoleInfo(u.role);
                const isMe = String(u.id) === String(me?.id);
                const isActive = u.status !== 'inactive';
                return (
                  <li key={u.id} className={`flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center ${!isActive ? 'opacity-60' : ''}`}>
                    {/* Avatar */}
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-sm uppercase">
                      {u.name?.charAt(0) ?? '?'}
                    </div>

                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-text">{u.name}</p>
                        {isMe && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">Tú</span>}
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${roleInfo.color}`}>
                          {roleInfo.label}
                        </span>
                        {!isActive && (
                          <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Inactivo</span>
                        )}
                      </div>
                      <p className="text-xs text-text-muted truncate">{u.email}{u.phone ? ` · ${u.phone}` : ''}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        title={isActive ? 'Desactivar' : 'Activar'}
                        disabled={isMe || toggleStatus.isPending}
                        onClick={() => toggleStatus.mutate({ id: String(u.id), status: isActive ? 'inactive' : 'active' })}
                        className="rounded-lg p-2 text-text-muted transition hover:bg-primary/10 hover:text-primary disabled:opacity-30"
                      >
                        {isActive ? <UserX size={16} /> : <UserCheck size={16} />}
                      </button>
                      <button
                        title="Cambiar contraseña"
                        onClick={() => setPwUser(u)}
                        className="rounded-lg p-2 text-text-muted transition hover:bg-primary/10 hover:text-primary"
                      >
                        <KeyRound size={16} />
                      </button>
                      <button
                        title="Editar"
                        onClick={() => { setEditUser(u); setShowForm(true); }}
                        className="rounded-lg p-2 text-text-muted transition hover:bg-primary/10 hover:text-primary"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        title="Eliminar"
                        disabled={isMe}
                        onClick={() => setConfirmDelete(u)}
                        className="rounded-lg p-2 text-text-muted transition hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

      </div>

      {/* Modals */}
      <UserFormModal
        open={showForm}
        onClose={() => { setShowForm(false); setEditUser(null); }}
        editUser={editUser}
      />
      <ChangePasswordModal user={pwUser} onClose={() => setPwUser(null)} />

      {/* Delete confirm */}
      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} titleId="confirm-delete-modal" className="max-w-sm">
        <div className="px-6 py-6 space-y-4">
          <h2 className="text-base font-semibold text-text">¿Eliminar usuario?</h2>
          <p className="text-sm text-text-muted">
            Estás a punto de eliminar a <span className="font-medium text-text">{confirmDelete?.name}</span>. Esta acción no se puede deshacer.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
            <Button
              onClick={() => deleteMutation.mutate(String(confirmDelete!.id))}
              isLoading={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
